import { useQuery } from '@apollo/client/react';
import { Box, Flex } from '@utilitywarehouse/hearth-react';
import { useEffect } from 'react';

import { FeatureFlags } from '@constants/featureFlags';
import { useIsBroadbandInstallationEnabled } from '@features/details/Details/BroadbandInstallation/hooks/useIsBroadbandInstallationEnabled';
import {
  AttributionDetails,
  type BroadbandAppointment,
  GetThankYouDataQuery,
  ThankYouBasketFragment,
} from '@generated/gql/graphql';
import { useFeatureFlag } from '@hooks/useFeatureFlag';

import { NextSteps } from './NextSteps';
import { PartnerBanner } from './PartnerBanner';
import { ReferAFriendBanner } from './ReferAFriendBanner';
import { Savings } from './Savings';
import { getRegistrationToken, reservedBroadbandAppointment } from './query';
import { useThankYouTracking } from './useThankYouTracking';

interface ThankYouUnwrappedProps {
  basket: Nullable<ThankYouBasketFragment>;
  attributionDetails: Nullable<AttributionDetails>;
  registrationToken: Nullable<string>;
  isPartnerBannerEnabled: boolean;
  broadbandAppointment?: Nullable<BroadbandAppointment>;
  isRegistrationTokenLoading: boolean;
}

interface ThankYouProps {
  data: Nullable<GetThankYouDataQuery>;
}

const ThankYouUnwrapped = ({
  basket,
  attributionDetails,
  registrationToken,
  isPartnerBannerEnabled,
  broadbandAppointment,
  isRegistrationTokenLoading,
}: ThankYouUnwrappedProps) => {
  const accountId = basket?.details?.accountId;
  const accountNumber = basket?.details?.accountNumber;
  const hasPartnerAssistant =
    !!attributionDetails?.assistant?.partner?.positionId;
  const hasPartnerReferrer =
    !!attributionDetails?.referrer?.partner?.positionId;
  const isPartnerApplication = hasPartnerAssistant || hasPartnerReferrer;
  const displayPartnerBanner =
    isPartnerApplication && isPartnerBannerEnabled && registrationToken;

  return (
    <Flex
      direction="column"
      spacing="2xl"
      marginBottom={{
        desktop: '600',
        tablet: '500',
        mobile: '400',
      }}
    >
      <NextSteps
        accountNumber={accountNumber}
        broadbandAppointment={broadbandAppointment}
      />
      {displayPartnerBanner && (
        <PartnerBanner registrationToken={registrationToken} />
      )}
      {!isPartnerApplication && accountId && !isRegistrationTokenLoading && (
        <ReferAFriendBanner accountId={accountId} />
      )}
      <Savings basket={basket ?? null} />
    </Flex>
  );
};

const ThankYou = ({ data }: ThankYouProps) => {
  const isBroadbandAppointmentBookingEnabled =
    useIsBroadbandInstallationEnabled(data?.basket?.selectedQuotes ?? []);
  const { value: isPartnerBannerEnabled } = useFeatureFlag(
    FeatureFlags.PARTNER_BANNER_ENABLED,
    false
  );
  const { data: registrationTokendata, loading: registrationTokenLoading } =
    useQuery(getRegistrationToken, {
      variables: { basketID: data?.basket?.id ?? '' },
      skip: !data?.basket?.id,
    });
  const { data: broadbandAppointmentData } = useQuery(
    reservedBroadbandAppointment,
    {
      variables: {
        basketId: data?.basket?.id ?? '',
      },
      fetchPolicy: 'cache-first', // we write to the cache when the appointment is reserved
      skip: !isBroadbandAppointmentBookingEnabled || !data?.basket?.id,
    }
  );

  useThankYouTracking(data);

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, []);

  return (
    <Box className="wrapper">
      <ThankYouUnwrapped
        basket={data?.basket ?? null}
        attributionDetails={data?.attributionDetails ?? null}
        isPartnerBannerEnabled={isPartnerBannerEnabled}
        broadbandAppointment={
          broadbandAppointmentData?.reservedBroadbandAppointment
        }
        registrationToken={
          registrationTokendata?.partnerRegistrationToken.token ?? null
        }
        isRegistrationTokenLoading={registrationTokenLoading}
      />
    </Box>
  );
};

export { ThankYou, ThankYouUnwrapped };
export type { ThankYouUnwrappedProps };
